/**
 * Announcer voice packs (DESIGN-client §8, docs/ANNOUNCER.md).
 *
 * `packages/server/scripts/announcer-generate.ts` writes one mp3 per spoken line plus a manifest;
 * `shell/announcerManifest.ts` fetches it and hands the flattened `{ id, url, … }` list here.
 * This module owns everything after that: the registry, the background fetch/decode into the
 * engine, and picking which clip a bark should speak.
 *
 * ## Picking a clip
 *
 * The `announcerBark` cue carries the *rendered* subtitle plus the `{team}`/`{n}` fills it was
 * rendered from. The generator renders the same templates with the same fills, so a registration's
 * `text` is exactly the string the cue will carry, and matching on that text is exact. The fills are
 * the fallback: a pack that holds only one variant per event misses two thirds of the bank's lines
 * by text, and the fills are what make a substitute line safe — same team, same number, different
 * wording. See `find`.
 *
 * ## Loading
 *
 * A full `host_v1` pack is ~167 clips. Registering does not block: clips are fetched and decoded
 * in the background at `PRELOAD_CONCURRENCY`, and a bark whose clip has not arrived yet simply
 * shows its subtitle, which is what happens with no voice pack at all.
 */
import type { AudioEngine } from './AudioEngine';
import { getAudioEngine } from './AudioEngine';

/** Structural mirror of `shell/announcerManifest.ts`'s registration (audio must not import shell). */
export interface AnnouncerClipRegistration {
  id: string;
  url: string;
  eventKey: string;
  variant: number;
  vars: { team?: string | number; n?: string | number };
  /** The rendered line, identical to the cue's `subtitle`. */
  text: string;
}

export interface RegisterOptions {
  /** `eager` (default) trickles every clip into the engine; `lazy` waits for first use. */
  preload?: 'eager' | 'lazy';
  engine?: Pick<AudioEngine, 'loadClip' | 'has'>;
}

export const PRELOAD_CONCURRENCY = 4;

/** Whitespace-insensitive, case-insensitive key for subtitle matching. */
export const textKey = (s: string): string => s.trim().replace(/\s+/g, ' ').toLowerCase();

const sameVar = (a: string | number | undefined, b: string | number | undefined): boolean =>
  a === undefined || b === undefined ? a === b : String(a) === String(b);

export interface ClipQuery {
  /** The cue's rendered subtitle; the primary key. */
  text?: string;
  /** Deterministic variant choice when there is no text (cue seq). */
  index?: number;
  vars?: AnnouncerClipRegistration['vars'];
}

export class AnnouncerClipRegistry {
  private readonly byId = new Map<string, AnnouncerClipRegistration>();
  /** `${eventKey} ${voiceId}` → registrations, in registration order. */
  private readonly byEvent = new Map<string, AnnouncerClipRegistration[]>();
  private readonly byText = new Map<string, AnnouncerClipRegistration>();
  private readonly loaded = new Set<string>();

  get size(): number {
    return this.byId.size;
  }

  ids(): string[] {
    return [...this.byId.keys()];
  }

  get(id: string): AnnouncerClipRegistration | null {
    return this.byId.get(id) ?? null;
  }

  /** `${eventKey}:${voiceId}` is the id prefix the generator uses; recover the voice from it. */
  private static voiceOf(reg: AnnouncerClipRegistration): string {
    const rest = reg.id.slice(reg.eventKey.length + 1);
    const hash = rest.indexOf('#');
    return hash < 0 ? rest : rest.slice(0, hash);
  }

  add(clips: readonly AnnouncerClipRegistration[]): void {
    for (const clip of clips) {
      if (this.byId.has(clip.id)) continue;
      this.byId.set(clip.id, clip);
      const key = `${clip.eventKey} ${AnnouncerClipRegistry.voiceOf(clip)}`;
      const list = this.byEvent.get(key);
      if (list) list.push(clip);
      else this.byEvent.set(key, [clip]);
      if (clip.text) this.byText.set(`${key} ${textKey(clip.text)}`, clip);
    }
  }

  markLoaded(id: string): void {
    this.loaded.add(id);
  }

  isLoaded(id: string): boolean {
    return this.loaded.has(id);
  }

  /**
   * The registration a bark should speak, or null when there is none.
   *
   * Exact subtitle text wins. When it misses — a pack holds only some of the bank's variants, so
   * this is the normal case rather than an edge one — the bark falls back to another variant of the
   * same event carrying the *same* `{team}`/`{n}` fills. Less variety, but a line that is still true
   * about the hand, which is the point: a wrong number or a wrong team is worse than silence.
   *
   * `sameVar` is strict about `undefined`, so a clip that carries fills is never picked for a query
   * that has none. An older server sends no `vars`, and so gets no fallback rather than some other
   * team's line.
   */
  find(eventKey: string, voiceId: string, q: ClipQuery = {}): AnnouncerClipRegistration | null {
    const key = `${eventKey} ${voiceId}`;
    if (q.text) {
      const hit = this.byText.get(`${key} ${textKey(q.text)}`);
      if (hit) return hit;
    }
    const all = this.byEvent.get(key);
    if (!all || all.length === 0) return null;
    const matching = all.filter((c) => sameVar(c.vars?.team, q.vars?.team) && sameVar(c.vars?.n, q.vars?.n));
    if (matching.length === 0) return null;
    if (q.index === undefined) return matching[0] ?? null;
    const variants = [...new Set(matching.map((c) => c.variant))].sort((a, b) => a - b);
    const variant = variants[((q.index % variants.length) + variants.length) % variants.length];
    return matching.find((c) => c.variant === variant) ?? null;
  }

  clear(): void {
    this.byId.clear();
    this.byEvent.clear();
    this.byText.clear();
    this.loaded.clear();
  }
}

export const announcerClips = new AnnouncerClipRegistry();

/** Fetch+decode `clips` into `engine`, at most `PRELOAD_CONCURRENCY` at a time. */
async function preload(
  clips: readonly AnnouncerClipRegistration[],
  engine: Pick<AudioEngine, 'loadClip' | 'has'>,
  registry: AnnouncerClipRegistry,
): Promise<number> {
  let next = 0;
  let ok = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const clip = clips[next++];
      if (!clip) return;
      if (await engine.loadClip(clip.id, clip.url)) {
        registry.markLoaded(clip.id);
        ok++;
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(PRELOAD_CONCURRENCY, clips.length) }, worker));
  return ok;
}

/**
 * Register a voice pack. Resolves as soon as the clips are known; the background load keeps
 * running (await `loading` if you need it, which the tests do). Never throws.
 */
export function registerAnnouncerClips(
  clips: readonly AnnouncerClipRegistration[],
  opts: RegisterOptions = {},
): { registered: number; loading: Promise<number> } {
  announcerClips.add(clips);
  if (opts.preload === 'lazy' || clips.length === 0) return { registered: clips.length, loading: Promise.resolve(0) };
  const engine = opts.engine ?? getAudioEngine();
  const loading = preload(clips, engine, announcerClips).catch((err: unknown) => {
    console.warn('[announcer] clip preload failed', err);
    return 0;
  });
  return { registered: clips.length, loading };
}

/**
 * Ensure one clip is in the engine, then report whether it can play now. Used by the lazy path
 * and by a bark that fires before the background load reached its line.
 */
export async function ensureAnnouncerClip(
  id: string,
  opts: { engine?: Pick<AudioEngine, 'loadClip' | 'has'> } = {},
): Promise<boolean> {
  const engine = opts.engine ?? getAudioEngine();
  if (engine.has(id)) return true;
  const reg = announcerClips.get(id);
  if (!reg) return false;
  const ok = await engine.loadClip(reg.id, reg.url);
  if (ok) announcerClips.markLoaded(reg.id);
  return ok;
}

/** Test seam: forget every registration. */
export function resetAnnouncerClips(): void {
  announcerClips.clear();
}
